import React, { useEffect, useState } from "react";
import { DatePicker, Typography, List, Skeleton, Tag, Tooltip, Empty } from "antd";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Area, AreaChart,
  Sector
} from "recharts";
import {
  TeamOutlined,
  UsergroupAddOutlined,
  UsergroupDeleteOutlined,
  UserSwitchOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CloudOutlined,
  TrophyOutlined,
  RiseOutlined,
  BarChartOutlined,
  PieChartOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { AiTwotoneNotification } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadHRMDashboardData } from "@/redux/rtk/features/dashboard/dashboardSlice";
import { loadAllAnnouncement } from "@/redux/rtk/features/hrm/announcement/announcementSlice";
import { loadAllPublicHoliday } from "@/redux/rtk/features/hrm/holiday/publicHolidaySlice";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";
import Loader from "@/components/Loader/Loader";
import { TrendingDown, TrendingUp, UserCheck, UserMinus, Users, UserX } from "lucide-react";
import Card from "@/UI/Card";
import Button from "@/UI/Button";
import { Link } from "react-router-dom";

const { RangePicker } = DatePicker;
const { Title } = Typography;

/* ---------- Custom Card Component ---------- */
const CustomCard = ({ children, className = "", icon, title, iconBg = "bg-blue-500" }) => (
  <div className={`bg-white border border-border rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-5 ${className}`}>
    {(icon || title) && (
      <div className="flex items-center gap-3 mb-4 ">
        {icon && (
          <div className={`${iconBg} w-10 h-10 flex items-center justify-center rounded-xl shadow-sm`}>
            {icon}
          </div>
        )}
        {title && <h3 className="text-lg font-semibold text-gray-800">{title}</h3>}
      </div>
    )}
    {children}
  </div>
);

/* ---------- Shared Empty State ---------- */
const NoData = ({ height = 240, text = "No data available" }) => (
  <div className="flex items-center justify-center" style={{ height }}>
    <Empty description={text} />
  </div>
);

/* ---------- Metric Cards ---------- */
const HRMDashboardCard = ({ information }) => {
  const cardData = [
    {
      title: "TOTAL USERS",
      value: information?.totalUsers || 0,
      icon: Users,
      variant: "default",
      subtitle: "All registered employees",
      trend: 100, // example % increase
    },
    {
      title: "TODAY PRESENT",
      value: information?.todayPresent || 0,
      icon: UserCheck,
      variant: "success",
      subtitle: "Checked in today",
      trend: information?.presentPercentage,
    },
    {
      title: "ON LEAVE",
      value: information?.todayOnLeave || 0,
      icon: UserMinus,
      variant: "warning",
      subtitle: "Currently on leave",
      trend: - information?.onLeavePercentage,
    },
    {
      title: "ABSENT",
      value: information?.todayAbsent || 0,
      icon: UserX,
      variant: "destructive",
      subtitle: "Did not check in",
      trend: - information?.absentPercentage,
    },
  ];

  const variantStyles = {
    default: "text-white",
    success: "text-white",
    warning: "text-white",
    destructive: "text-white",
  };
  const variantBg = {
    default: "bg-blue-400",
    success: "bg-emerald-400",
    warning: "bg-amber-400",
    destructive: "bg-rose-400",
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cardData.map((item, i) => {
        const Icon = item.icon;
        const isPositive = item.trend && item.trend > 0;
        const trendColor = "text-white/90";

        return (
          <Card
            key={i}
            className={`p-6 border border-border ${variantBg[item.variant]} shadow-card hover:shadow-card-hover transition-all duration-300 animate-fade-in rounded-2xl`}
          >
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white/80">{item.title}</p>
                <Icon className={`h-5 w-5 ${variantStyles[item.variant]}`} />
              </div>

              {/* Value & Trend */}
              <div className="space-y-1">
                <div className="flex items-end gap-2">
                  <h3 className="text-3xl font-bold tracking-tight text-white">{Number(item.value).toLocaleString()}</h3>
                  {item.trend !== undefined && (
                    <div className={`flex items-center gap-1 mb-1 ${trendColor}`}>
                      {isPositive ? (
                        <TrendingUp className="h-4 w-4" />
                      ) : (
                        <TrendingDown className="h-4 w-4" />
                      )}
                      <span className="text-sm font-medium">
                        {Math.abs(item.trend)}%
                      </span>
                    </div>
                  )}
                </div>

                {item.subtitle && (
                  <p className="text-xs text-white/70">{item.subtitle}</p>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

/* ---------- Custom Tooltip Components ---------- */

const CustomLineTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-100">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-semibold text-indigo-600">{payload[0].value.toFixed(2)} hours</p>
      </div>
    );
  }
  return null;
};

const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-100">
        <p className="text-xs text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-semibold text-purple-600">{payload[0].value} leaves</p>
      </div>
    );
  }
  return null;
};
// -------- AttendancePie ----------
const useIsMobile = (query = "(max-width: 640px)") => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const m = window.matchMedia(query);
    const onChange = (e) => setIsMobile(e.matches);
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, [query]);
  return isMobile;
};

const renderActiveShapeDesktop = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,
}) => {
  const RAD = Math.PI / 180;
  const sin = Math.sin(-RAD * (midAngle ?? 0));
  const cos = Math.cos(-RAD * (midAngle ?? 0));
  const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
  const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
  const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
  const my = (cy ?? 0) + ((outerRadius ?? 0) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const anchor = cos >= 0 ? "start" : "end";

  return (
    <g>
      {/* Main sector + halo */}
      <Sector {...{ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }} />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 6}
        outerRadius={(outerRadius ?? 0) + 10}
        fill={fill}
      />

      {/* Center label (title only) */}
      <text x={cx} y={cy} dy={4} textAnchor="middle" fill={fill} className="font-semibold">
        {payload?.name}
      </text>

      {/* Callout */}
      <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
      <circle cx={ex} cy={ey} r={2} fill={fill} />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={anchor}
        fill="#334155"
        className="font-semibold"
      >
        {value} Employees
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={16}
        textAnchor={anchor}
        fill="#94a3b8"
      >
        {`${((percent ?? 0) * 100).toFixed(1)}%`}
      </text>
    </g>
  );
};

const renderActiveShapeMobile = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  payload,
  percent,
  value,
}) => {
  return (
    <g>
      {/* Main sector + subtle halo (no callout lines) */}
      <Sector {...{ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }} />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={(outerRadius ?? 0) + 4}
        outerRadius={(outerRadius ?? 0) + 8}
        fill={fill}
      />

      {/* Stacked, centered text with line breaks */}
      <text x={cx} y={cy} textAnchor="middle" className="font-semibold" fill="#0f172a">
        <tspan x={cx} dy="-6" fontWeight="600" fill={fill}>
          {payload?.name}
        </tspan>
        <tspan x={cx} dy="18" fontWeight="600">
          {value} Employees
        </tspan>
        <tspan x={cx} dy="16" fill="#94a3b8">
          {`${((percent ?? 0) * 100).toFixed(1)}%`}
        </tspan>
      </text>
    </g>
  );
};

const AttendancePie = ({ data }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const isMobile = useIsMobile(); // <=640px

  const present = data?.todayPresent ?? 0;
  const onLeave = data?.todayOnLeave ?? 0;
  const absent = data?.todayAbsent ?? 0;
  const total = present + onLeave + absent;
  if (total === 0) return <NoData height={280} />;

  const chartData = [
    { name: "Absent", value: absent, color: "#f43f5e" },
    { name: "On Leave", value: onLeave, color: "#a855f7" },
    { name: "Present", value: present, color: "#10b981" },
  ];
  const COLORS = chartData.map((c) => c.color);

  // On touch screens, toggle active by tap; on desktop, use hover
  const handleEnter = (_, i) => !isMobile && setActiveIndex(i);
  const handleLeave = () => !isMobile && setActiveIndex(-1);
  const handleClick = (_, i) => isMobile && setActiveIndex(i);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={isMobile ? 56 : 70}
          outerRadius={isMobile ? 84 : 100}
          dataKey="value"
          activeIndex={activeIndex}
          activeShape={isMobile ? renderActiveShapeMobile : renderActiveShapeDesktop}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={handleClick}
          isAnimationActive
          animationDuration={700}
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>

        {/* Legend: row on desktop, column on mobile */}
        <Legend
          content={({ payload = [] }) => (
            <div
              className={`flex ${isMobile ? "flex-col items-center gap-2" : "flex-row flex-wrap justify-center gap-4"
                } mt-4 px-4`}
            >
              {payload.map((entry, index) => (
                <div
                  key={`legend-${index}`}
                  className="flex items-center gap-2 cursor-pointer transition-transform hover:scale-105"
                  onMouseEnter={() => !isMobile && setActiveIndex(index)}
                  onMouseLeave={() => !isMobile && setActiveIndex(-1)}
                  onClick={() => isMobile && setActiveIndex(index)}
                >
                  <span
                    className="w-3 h-3 rounded-full shadow-sm inline-block"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {entry.value}
                  </span>
                  <span className="text-xs text-gray-500 font-semibold">
                    ({entry.payload?.value})
                  </span>
                </div>
              ))}
            </div>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
// -------- end AttendancePie ----------


/* ---------- Monthly Leave Bar Chart ---------- */
const MonthlyLeaveChart = ({ data, loading }) => {
  const chartData = data?.monthlyLeaveChart || [];
  if (loading) return <Loader />;
  if (chartData.length === 0) return <NoData height={280} />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
        <RechartsTooltip content={<CustomBarTooltip />} />
        <Bar dataKey="total" fill="#a855f7" radius={[8, 8, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ---------- Work Hours Line Chart ---------- */
const WorkHoursChart = ({ chartData, loading }) => {
  if (loading) return <Loader />;
  if (chartData.length === 0) return <NoData height={280} />;

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} />
        <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
        <RechartsTooltip content={<CustomLineTooltip />} />
        <Area
          type="monotone"
          dataKey="totalHours"
          stroke="#6366f1"
          strokeWidth={3}
          fillOpacity={1}
          fill="url(#colorHours)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};


/* ---------- ProjectRatioPie ---------- */
// const CustomPieTooltip = ({ active, payload }) => {
//   if (active && payload && payload.length) {
//     const { name, value, payload: item } = payload[0];
//     return (
//       <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-100">
//         <p className="text-sm font-semibold text-gray-800">
//           {name}
//         </p>
//         <p className="text-sm text-gray-600">
//           {value} {value === 1 ? "project" : "projects"}
//         </p>
//       </div>
//     );
//   }
//   return null;
// };

const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0];
    return (
      <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-100">
        <p className="text-sm font-semibold text-gray-800">{name}</p>
        <p className="text-sm text-gray-600">
          {value} {value === 1 ? "project" : "projects"}
        </p>
      </div>
    );
  }
  return null;
};

// Your ProjectRatioPie component (with updates)
const ProjectRatioPie = ({ data }) => {
  const isMobile = useIsMobile(); // 2. Use the hook

  const pending = data?.find((item) => item.name === "PENDING")?.value ?? 0;
  const complete = data?.find((item) => item.name === "COMPLETE")?.value ?? 0;
  const onHold = data?.find((item) => item.name === "ONHOLD")?.value ?? 0;
  const deleted = data?.find((item) => item.name === "DELETED")?.value ?? 0;

  const total = pending + complete + onHold + deleted;

  if (total === 0) return <NoData height={280} />;

  const chartData = [
    { name: "Pending", value: pending, color: "#3b82f6" },
    { name: "Complete", value: complete, color: "#10B981" },
    { name: "On Hold", value: onHold, color: "#A67A7A" },
    { name: "Deleted", value: deleted, color: "#F43F5E" },
  ];

  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={70}
          outerRadius={100}
          paddingAngle={2}
          dataKey="value"
          // 3. Conditionally set label and labelLine
          labelLine={!isMobile}
          label={
            !isMobile
              ? ({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`
              : false
          }
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <RechartsTooltip content={<CustomPieTooltip />} />
        <Legend
          verticalAlign="bottom"
          height={36}
          iconType="circle"
          formatter={(value) => (
            <span className="text-sm text-gray-700">{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};
/* ---------- end ProjectRatioPie ---------- */

/* ---------- Main Dashboard ---------- */
const Dashboard = () => {
  const dispatch = useDispatch();
  const { hrmInfo: data, loading } = useSelector((s) => s.dashboard);
  const { list: announcements, loading: aLoading } = useSelector((s) => s.announcement);
  const { list: holidays, loading: hLoading } = useSelector((s) => s.publicHoliday);

  const [pageConfig, setPageConfig] = useState({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
  });

  useEffect(() => {
    dispatch(loadHRMDashboardData(pageConfig));
    dispatch(loadAllAnnouncement({ query: "all" }));
    dispatch(loadAllPublicHoliday());
  }, [dispatch, pageConfig]);

  const onCalendarChange = (dates) => {
    const s = dates?.[0]?.format("YYYY-MM-DD");
    const e = dates?.[1]?.format("YYYY-MM-DD");
    if (s && e) setPageConfig({ startDate: s, endDate: e });
  };

  const chartData =
    data?.workHoursByDate?.map((d) => {
      const [h = 0, m = 0, s = 0] = String(d?.time || "0:0:0").split(":").map(Number);
      return { ...d, totalHours: h + m / 60 + s / 3600 };
    }) || [];

  return (
    <UserPrivateComponent permission="readAll-dashboard">
      <div className="p-4 sm:p-6 min-h-screen bg-white">
        <div className="max-w-8xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">HRM Dashboard</h1>
              <p className="text-gray-500 text-sm mt-1">Welcome back! Here's your HR summary.</p>
            </div>
            <div className="bg-white rounded-xl px-4 py-2 shadow-sm border border-gray-200">
              <RangePicker
                onCalendarChange={onCalendarChange}
                defaultValue={[dayjs().startOf("month"), dayjs().endOf("month")]}
                format="DD MMM"
                bordered={false}
              />
            </div>
          </div>

          <HRMDashboardCard information={data} />

          {/* Main Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <CustomCard
              // icon={<RiseOutlined className="text-white text-lg" />}
              title="Work Hours"
              iconBg="bg-gradient-to-br from-indigo-400 to-indigo-600"
            >
              <WorkHoursChart chartData={chartData} loading={loading} />
            </CustomCard>


            <CustomCard
              // icon={<PieChartOutlined className="text-white text-lg" />}
              title="Project Ratio"
              iconBg="bg-gradient-to-br from-pink-400 to-pink-600"
            >
              <ProjectRatioPie data={data?.projectRatioChart || []} />
            </CustomCard>


          </div>
          {/* HR Insights Section */}
          <div className="mt-8">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <CustomCard
                // icon={<TrophyOutlined className="text-white text-lg" />}
                title="Today's Attendance"
                iconBg="bg-gradient-to-br from-emerald-400 to-emerald-600"
              >
                {loading ? <Loader /> : <AttendancePie data={data} />}
              </CustomCard>
              <CustomCard
                // icon={<CalendarOutlined className="text-white text-lg" />}
                title="Monthly Leave"
                iconBg="bg-gradient-to-br from-purple-400 to-purple-600"
              >
                <MonthlyLeaveChart data={data} loading={loading} />
              </CustomCard>
            </div>
          </div>

          {/* Announcements + Holidays */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Announcements */}
            <CustomCard className="overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Announcements</h3>

                <Link
                  to="/admin/announcement"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1"
                >
                  View all
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>


              {aLoading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (announcements || []).length === 0 ? (
                <NoData height={200} text="No announcements" />
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  <List
                    itemLayout="horizontal"
                    dataSource={announcements.slice(0, 3)}
                    renderItem={(item) => (
                      <List.Item className="transition-all duration-200 rounded-xl my-1 px-3 py-2 border border-gray-100  ">
                        <List.Item.Meta
                          title={
                            <div className="flex justify-between items-start flex-wrap gap-2">
                              <p className="text-base font-semibold text-gray-800 truncate">
                                {item.title || "Untitled Announcement"}
                              </p>
                              {item.createdAt && (
                                <p className="text-xs text-gray-400 flex-shrink-0">
                                  {dayjs(item.createdAt).format("DD MMM, YYYY")}
                                </p>
                              )}
                            </div>
                          }
                          description={
                            <p className="text-sm text-gray-600 mt-1 leading-relaxed line-clamp-2">
                              {item.description || "No description provided."}
                            </p>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </CustomCard>

            {/* Public Holidays */}
            <CustomCard className="overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Public Holidays</h3>

                <Link
                  to="/admin/public-holiday"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline inline-flex items-center gap-1"
                >
                  View all
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>


              {hLoading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (holidays || []).length === 0 ? (
                <NoData height={200} text="No upcoming public holidays" />
              ) : (
                <div className="max-h-[300px] overflow-y-auto">
                  <List
                    itemLayout="horizontal"
                    dataSource={holidays.slice(0, 3)}
                    renderItem={(item) => {
                      const isRange = item.startDate !== item.endDate;
                      return (
                        <List.Item className="transition-all duration-200 rounded-xl my-1 px-3 py-2 border border-gray-100  ">
                          <List.Item.Meta
                            title={
                              <div className="flex items-start justify-between w-full gap-2">
                                <div className="min-w-0">
                                  <p className="text-base font-semibold text-gray-800 truncate">
                                    {item.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {dayjs(item.startDate).format("DD MMM, YYYY")}
                                    {isRange && <> – {dayjs(item.endDate).format("DD MMM, YYYY")}</>}
                                  </p>
                                </div>
                                <Tooltip title="Public Holiday">
                                  <Tag
                                    color="blue"
                                    className="bg-blue-50 text-blue-600 border-blue-200 rounded-lg flex items-center"
                                  >
                                    <CloudOutlined className="mr-1" />
                                    Holiday
                                  </Tag>
                                </Tooltip>
                              </div>
                            }
                          />
                        </List.Item>
                      );
                    }}
                  />
                </div>
              )}
            </CustomCard>
          </div>

        </div>
      </div>
    </UserPrivateComponent>
  );
};

export default Dashboard;